<template>
  <div>
    <Toast />
    <div class="main-banner">
      <div class="main-banner-content">
        <div class="banner-text">
          <div class="banner-title">
            {{ $t('mainPage.banner[0]') }}
          </div>
          <div class="banner-description">
            {{ $t('mainPage.banner[1]') }}
          </div>
          <div class="banner-action">
            <NuxtLink :to="localePath('/viber-business-messages')" class="nav-link button filled">
              <p>{{ $t('mainPage.banner[2]') }}</p>
            </NuxtLink>
            <NuxtLink to="viber://chat?service=26076" class="button outlined" external>{{ $t('mainPage.banner[3]') }}
            </NuxtLink>
          </div>
        </div>
        <div class="banner-img">
          <div class="banner-img-bg"></div>
          <div class="banner-img-phone">
            <NuxtImg src="/images/banner-iphone.webp" width="254" height="514" format="webp" loading="eager" fetchpriority="high" alt="SMSBAT Omnichannel Inbox Interface" class="object-contain" />
          </div>
          <div class="banner-img-man">
            <NuxtImg src="/images/banner-man.webp" width="700" height="938" format="webp" loading="eager" fetchpriority="high" alt="Customer Support Agent" class="object-contain" />
          </div>
          <div class="banner-img-text banner-img-text-1">
            {{ $t('mainPage.banner[4]') }}<span class="highlight-blue">{{ $t('mainPage.banner[5]') }}</span>
            {{ $t('mainPage.banner[6]') }}
          </div>
          <div class="banner-img-text banner-img-text-2">
            {{ $t('mainPage.banner[7]') }}<span class="highlight-blue">{{ $t('mainPage.banner[8]') }}</span>
            {{ $t('mainPage.banner[9]') }}
          </div>
          <div class="banner-img-text banner-img-text-3">
            {{ $t('mainPage.banner[10]') }}
            <span class="highlight-blue">{{ $t('mainPage.banner[11]') }}</span>
          </div>
        </div>
      </div>
    </div>

    <div class="page-section description-section">
      <div class="container">
        <div class="mail-bg-img"></div>
        <div class="phone-bg-img"></div>
        <div class="section-content">
          <div class="text-content">
            <p>
              {{ $t('mainPage.subTitle[0]') }}
              <span class="highlight-blue">{{ $t('mainPage.subTitle[1]') }}</span>{{ $t('mainPage.subTitle[2]') }}
              <span class="highlight-blue">{{ $t('mainPage.subTitle[3]') }}</span>{{ $t('mainPage.subTitle[4]') }}
              <span class="highlight-blue">{{ $t('mainPage.subTitle[5]') }}</span>{{ $t('mainPage.subTitle[6]') }}
            </p>
            <p>
              {{ $t('mainPage.subTitle[7]') }} <span class="highlight-blue">{{ $t('mainPage.subTitle[8]') }}</span>
              {{ $t('mainPage.subTitle[9]') }}
              <span class="highlight-blue">{{ $t('mainPage.subTitle[10]') }}</span>.
            </p>
            <p>
              {{ $t('mainPage.subTitle[11]') }}
              <span class="highlight-blue">{{ $t('mainPage.subTitle[12]') }}</span>{{ $t('mainPage.subTitle[13]') }}
            </p>
            <p>
              {{ $t('mainPage.subTitle[14]') }}
              <span class="highlight-blue">{{ $t('mainPage.subTitle[15]') }}</span>.
            </p>
          </div>
        </div>
      </div>
    </div>

    <div class="page-section">
      <div class="container">
        <div class="section-head">
          <div class="section-head-title">{{ $t('mainPage.about.title') }}?</div>
        </div>
        <div class="section-content">
          <div class="text-content">
            <p>
              <span class="highlight-name">{{ $t('mainPage.about.subTitle[0]') }}</span>{{
                $t('mainPage.about.subTitle[1]') }}
            </p>
          </div>

          <div class="about-section">
            <div class="about-item">
              <div class="about-item-text">
                {{ $t('mainPage.about.blocks[0][0]') }}
                <span class="highlight-blue">{{ $t('mainPage.about.blocks[0][1]') }}</span>{{
                  $t('mainPage.about.blocks[0][2]') }}
              </div>
              <div class="about-item-img">
                <NuxtImg src="/images/about-1.webp" width="391" height="254" format="webp" loading="lazy" alt="Global Coverage Feature" class="w-full h-auto object-contain" />
              </div>
            </div>
            <div class="about-item">
              <div class="about-item-img">
                <NuxtImg src="/images/about-2.webp" width="391" height="254" format="webp" loading="lazy" alt="Omnichannel Capabilities" class="w-full h-auto object-contain" />
              </div>
              <div class="about-item-text">
                {{ $t('mainPage.about.blocks[1][0]') }}
                <span class="highlight-blue">{{ $t('mainPage.about.blocks[1][1]') }}</span>
                {{ $t('mainPage.about.blocks[1][2]') }}
              </div>
            </div>
            <div class="about-item">
              <div class="about-item-text">
                {{ $t('mainPage.about.blocks[2][0]') }}
                <span class="highlight-blue">{{ $t('mainPage.about.blocks[2][1]') }}</span>{{
                  $t('mainPage.about.blocks[2][2]') }}
              </div>
              <div class="about-item-img">
                <NuxtImg src="/images/about-3.webp" width="391" height="254" format="webp" loading="lazy" alt="API and Integrations" class="w-full h-auto object-contain" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <PriceSection />
    <PopularQuestions />
    <ContactForm :imgStartIndex="5" />
  </div>
</template>

<script setup lang="ts">
const localePath = useLocalePath();
const { t } = useI18n();

useHead({
  titleTemplate: () => t("seo.layouts./.title") || t("seo.title"),
  link: [
    { rel: 'preload', as: 'image', href: '/images/banner-bg.png' },
    { rel: 'preload', as: 'image', href: '/images/banner-iphone.webp' }
  ],
  meta: [
    { name: "description", content: "Optimize the online store's operation, bulk Viber & SMS mailings from the SMSBAT. Ensure efficient customer communication anywhere in the world." },
    { property: "og:title", content: "Viber message rates & bulk SMS messaging via internet | SMSBAT" },
    { property: "og:description", content: "Optimize the online store's operation, bulk Viber & SMS mailings from the SMSBAT. Ensure efficient customer communication anywhere in the world." },
    { property: "og:image", content: "https://smsbat.com/images/banner-iphone.webp" },
    { property: "og:type", content: "website" },
    { name: "twitter:card", content: "summary_large_image" }
  ]
})
</script>

<style lang="scss" scoped>
.main-banner .banner-text {
  margin-left: 0;
  align-self: center;
  max-width: 700px;
}

.main-banner-content {
  padding: 0;
}

@media (min-width: 768px) {
  .main-banner .banner-text {
    max-width: 500px;
  }
}

@media (min-width: 1170px) {
  .main-banner .banner-text {
    max-width: 500px;
  }
}

@media (min-width: 1300px) {
  .main-banner .banner-text {
    margin-left: calc((100% - 1300px) / 2);
    max-width: 700px;
  }
}
</style>